import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Package, Warning, Clock, FirstAid, Cube, BookOpen, CurrencyInr, ChartBar, Factory, Lightning, Fire } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import IsoReadinessWidget from "@/components/inventory/IsoReadinessWidget";
import StoreQueueWidget from "@/components/inventory/StoreQueueWidget";

const card =
  "bg-white border border-slate-200 rounded-lg p-5 hover:shadow-sm transition";

function Kpi({ label, value, icon: Icon, tone = "emerald", to, testId }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
    slate: "bg-slate-100 text-slate-700",
    indigo: "bg-indigo-50 text-indigo-700",
  };
  const inner = (
    <div className={card} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
            {label}
          </div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2 leading-none">
            {value}
          </div>
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
          <Icon size={20} weight="duotone" />
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function InventoryDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inventory/dashboard");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading inventory…</div>;
  }
  if (!data) {
    return <div className="text-sm text-rose-600">Failed to load dashboard.</div>;
  }

  const fmtINR = (n) =>
    "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6" data-testid="inv-dashboard">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
          Inventory & ERP
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">
          Production Floor Overview
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Live signal from your store, recipes, and batches. Refreshes on every page load.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Inventory Value"
          value={fmtINR(data.inventory_value)}
          icon={CurrencyInr}
          tone="emerald"
          testId="kpi-inventory-value"
        />
        <Kpi
          label="Items Available"
          value={`${data.items_available} / ${data.items_total}`}
          icon={Cube}
          tone="sky"
          to="/inventory/items"
          testId="kpi-items-available"
        />
        <Kpi
          label="Low Stock"
          value={data.low_stock_count}
          icon={Warning}
          tone="amber"
          testId="kpi-low-stock"
        />
        <Kpi
          label="Out of Stock"
          value={data.out_of_stock_count}
          icon={FirstAid}
          tone="rose"
          testId="kpi-out-of-stock"
        />
        <Kpi
          label="Near Expiry (30d)"
          value={data.near_expiry_count}
          icon={Clock}
          tone="amber"
          testId="kpi-near-expiry"
        />
        <Kpi
          label="Expired"
          value={data.expired_count}
          icon={Warning}
          tone="rose"
          testId="kpi-expired"
        />
        <Kpi
          label="Recipes Pending"
          value={data.recipes_pending}
          icon={BookOpen}
          tone="indigo"
          to="/inventory/recipes"
          testId="kpi-recipes-pending"
        />
        <Kpi
          label="Stock Health"
          value={
            data.out_of_stock_count === 0 && data.low_stock_count === 0 ? "Healthy" : "Action"
          }
          icon={ChartBar}
          tone={data.out_of_stock_count === 0 && data.low_stock_count === 0 ? "emerald" : "amber"}
          testId="kpi-stock-health"
        />
      </div>

      {/* Store approval queue (shown only to Store/admin roles) */}
      <StoreQueueWidget />

      {/* ISO 22000 Readiness widget */}
      <IsoReadinessWidget />

      {/* Production Today control room */}
      <ProductionToday data={data.production_today} />

      {/* Live cost heatmap */}
      <CostHeatmap data={data.cost_heatmap} />

      {/* Detail panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          title="Out of stock"
          subtitle="Order these immediately"
          empty="Nothing out of stock. Good day."
          items={data.out_of_stock}
          renderRow={(it) => (
            <tr key={it.id} className="border-t border-slate-100">
              <td className="px-4 py-2 text-sm font-medium text-slate-900">{it.name}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{it.code}</td>
              <td className="px-4 py-2 text-sm text-rose-600 text-right">0 {it.uom || ""}</td>
            </tr>
          )}
          headers={["Item", "Code", "Available"]}
          testId="panel-out-of-stock"
        />
        <Panel
          title="Low stock"
          subtitle="Below reorder level"
          empty="No reorders pending."
          items={data.low_stock}
          renderRow={(it) => (
            <tr key={it.id} className="border-t border-slate-100">
              <td className="px-4 py-2 text-sm font-medium text-slate-900">{it.name}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{it.code}</td>
              <td className="px-4 py-2 text-sm text-amber-700 text-right">
                {it.qty} / {it.reorder_level} {it.uom || ""}
              </td>
            </tr>
          )}
          headers={["Item", "Code", "Qty / Reorder"]}
          testId="panel-low-stock"
        />
        <Panel
          title="Expired batches"
          subtitle="Quarantine or dispose"
          empty="No expired stock."
          items={data.expired}
          renderRow={(s) => (
            <tr key={s.id} className="border-t border-slate-100">
              <td className="px-4 py-2 text-sm font-medium text-slate-900">{s.batch_no}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{s.location}</td>
              <td className="px-4 py-2 text-sm text-rose-600 text-right">{s.expiry_date}</td>
            </tr>
          )}
          headers={["Batch", "Location", "Expiry"]}
          testId="panel-expired"
        />
        <Panel
          title="Near expiry (30 days)"
          subtitle="Plan FEFO consumption"
          empty="No items expiring soon."
          items={data.near_expiry}
          renderRow={(s) => (
            <tr key={s.id} className="border-t border-slate-100">
              <td className="px-4 py-2 text-sm font-medium text-slate-900">{s.batch_no}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{s.location}</td>
              <td className="px-4 py-2 text-sm text-amber-700 text-right">{s.expiry_date}</td>
            </tr>
          )}
          headers={["Batch", "Location", "Expiry"]}
          testId="panel-near-expiry"
        />
      </div>
    </div>
  );
}

function Panel({ title, subtitle, items, headers, renderRow, empty, testId }) {
  return (
    <div className={card} data-testid={testId}>
      <div className="mb-3">
        <div className="font-heading text-base font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-500 px-1 py-6 text-center">{empty}</div>
      ) : (
        <table className="w-full">
          <thead>
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold px-4 py-2 text-left last:text-right"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{items.map(renderRow)}</tbody>
        </table>
      )}
    </div>
  );
}


function ProductionToday({ data }) {
  if (!data) return null;
  const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const statusTone = {
    planned: "bg-slate-100 text-slate-600",
    reserved: "bg-amber-50 text-amber-700",
    issuing: "bg-sky-50 text-sky-700",
    in_progress: "bg-sky-50 text-sky-700",
  };
  const varianceTone =
    data.avg_variance_pct === 0
      ? "text-slate-700"
      : data.avg_variance_pct < -2
      ? "text-rose-700"
      : data.avg_variance_pct < 0
      ? "text-amber-700"
      : "text-emerald-700";

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="production-today">
      <div className="px-5 py-4 bg-gradient-to-r from-emerald-50 to-white border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-600 text-white flex items-center justify-center">
            <Factory size={20} weight="duotone" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold">Production Control Room</div>
            <div className="font-heading text-base font-semibold text-slate-900">Today&apos;s floor</div>
          </div>
        </div>
        <Link to="/inventory/production-runs" className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid="production-today-all-link">
          View all runs →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-b border-slate-200">
        <Stat label="In progress" value={data.in_progress_count} icon={Lightning} tone="sky" testId="pt-in-progress" />
        <Stat label="Planned today" value={data.planned_count} sub={`yield ${data.planned_yield_kg} kg`} testId="pt-planned-count" />
        <Stat label="Planned cost" value={fmtINR(data.planned_cost)} testId="pt-planned-cost" />
        <Stat label="Completed today" value={data.completed_count} sub={`yield ${data.actual_yield_kg} kg`} tone="emerald" testId="pt-completed-count" />
        <Stat
          label="Avg variance"
          value={data.completed_count > 0 ? `${data.avg_variance_pct > 0 ? "+" : ""}${data.avg_variance_pct}%` : "—"}
          tone={data.completed_count === 0 ? "slate" : data.avg_variance_pct < -2 ? "rose" : data.avg_variance_pct < 0 ? "amber" : "emerald"}
          valueClass={varianceTone}
          testId="pt-variance"
        />
      </div>

      <div>
        {data.in_progress.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-2">
              <Factory size={18} weight="duotone" />
            </div>
            <div className="text-sm text-slate-700 font-medium">No runs in progress.</div>
            <div className="text-xs text-slate-500 mt-1">
              <Link to="/inventory/production-runs" className="text-emerald-700 font-medium hover:underline">Start a production run</Link> to put materials in motion.
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Code", "Recipe", "Batch", "Status", "Materials Used", "Cost", ""].map((h) => (
                  <th key={h} className="text-left px-5 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.in_progress.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pt-row-${r.id}`}>
                  <td className="px-5 py-3 text-xs font-mono text-slate-700">{r.code}</td>
                  <td className="px-5 py-3 text-sm font-medium text-slate-900">{r.recipe_name}</td>
                  <td className="px-5 py-3 text-sm tabular-nums">{r.batch_size} kg</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${statusTone[r.status] || "bg-slate-100 text-slate-600"}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-600 tabular-nums">{r.materials_consumed} batches</td>
                  <td className="px-5 py-3 text-xs text-slate-600 tabular-nums">
                    {r.actual_material_cost ? fmtINR(r.actual_material_cost) : <span className="text-slate-400">planned {fmtINR(r.total_material_cost)}</span>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/inventory/production-runs/${r.id}`} className="text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid={`pt-open-${r.id}`}>
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, tone = "slate", valueClass, testId }) {
  const toneMap = {
    sky: "text-sky-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
    slate: "text-slate-900",
  };
  return (
    <div className="px-5 py-4 border-r border-slate-100 last:border-r-0 flex items-center gap-3" data-testid={testId}>
      {Icon && (
        <div className={`w-8 h-8 rounded ${tone === "sky" ? "bg-sky-50 text-sky-600" : "bg-slate-100 text-slate-500"} flex items-center justify-center shrink-0`}>
          <Icon size={16} weight="bold" />
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
        <div className={`text-lg font-bold leading-tight mt-0.5 ${valueClass || toneMap[tone]}`}>{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function CostHeatmap({ data }) {
  if (!data) return null;
  const rows = data.rows || [];
  const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const maxCost = rows.length > 0 ? Math.max(...rows.map((r) => r.total_cost)) : 0;

  // Heatmap colour ramp keyed off share-of-spend
  const barTone = (share) => {
    if (share >= 25) return "from-rose-500 to-rose-400 text-white";
    if (share >= 15) return "from-amber-500 to-amber-400 text-white";
    if (share >= 7)  return "from-sky-500 to-sky-400 text-white";
    return "from-emerald-500 to-emerald-400 text-white";
  };
  const chipTone = (share) => {
    if (share >= 25) return "bg-rose-50 text-rose-700";
    if (share >= 15) return "bg-amber-50 text-amber-700";
    if (share >= 7)  return "bg-sky-50 text-sky-700";
    return "bg-emerald-50 text-emerald-700";
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cost-heatmap">
      <div className="px-5 py-4 bg-gradient-to-r from-rose-50 to-white border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-rose-600 text-white flex items-center justify-center">
            <Fire size={20} weight="duotone" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700 font-bold">Live Cost Heatmap</div>
            <div className="font-heading text-base font-semibold text-slate-900">
              Top 10 ingredients eating today&apos;s margin
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Top-10 spend</div>
          <div className="text-lg font-bold text-slate-900" data-testid="heatmap-total">{fmtINR(data.total_cost_today)}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-2">
            <Fire size={18} weight="duotone" />
          </div>
          <div className="text-sm text-slate-700 font-medium">No production consumption today yet.</div>
          <div className="text-xs text-slate-500 mt-1">
            Issue a <Link to="/inventory/production-runs" className="text-emerald-700 font-medium hover:underline">production run</Link> to see your live cost heatmap appear here.
          </div>
        </div>
      ) : (
        <div className="px-5 py-4 space-y-2">
          {rows.map((r, idx) => (
            <div
              key={r.item_id}
              className="grid grid-cols-12 items-center gap-3 group"
              data-testid={`heatmap-row-${r.item_id}`}
            >
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <span className="text-[10px] font-mono text-slate-400 w-5">{String(idx + 1).padStart(2, "0")}</span>
                <span className="text-sm font-medium text-slate-900 truncate" title={r.item_name}>{r.item_name}</span>
              </div>
              <div className="col-span-6">
                <div className="relative h-7 bg-slate-50 rounded overflow-hidden">
                  <div
                    className={`h-full rounded bg-gradient-to-r ${barTone(r.share_pct)} flex items-center justify-end pr-3 transition-all`}
                    style={{ width: `${maxCost > 0 ? Math.max(5, (r.total_cost / maxCost) * 100) : 5}%` }}
                  >
                    <span className="text-[11px] font-bold tabular-nums">{fmtINR(r.total_cost)}</span>
                  </div>
                </div>
              </div>
              <div className="col-span-2 text-xs text-slate-500 tabular-nums text-right">
                {r.qty_consumed} {r.uom} <span className="text-slate-400">· {r.batches} runs</span>
              </div>
              <div className="col-span-1 text-right">
                <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded ${chipTone(r.share_pct)}`}>
                  {r.share_pct}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
          <div className="flex items-center gap-3">
            <LegendChip tone="rose" label="≥25%" />
            <LegendChip tone="amber" label="15-25%" />
            <LegendChip tone="sky" label="7-15%" />
            <LegendChip tone="emerald" label="< 7%" />
          </div>
          <div className="text-slate-400">share of top-10 spend (today, UTC)</div>
        </div>
      )}
    </div>
  );
}

function LegendChip({ tone, label }) {
  const map = {
    rose: "bg-rose-500",
    amber: "bg-amber-500",
    sky: "bg-sky-500",
    emerald: "bg-emerald-500",
  };
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded-sm ${map[tone]}`} />
      <span>{label}</span>
    </div>
  );
}

